import type Phaser from 'phaser';
import type { MowerId } from '../../content/mowers';
import { Dir } from '../../sim/direction';
import { drawMowerFrame, mowerFrameSize, type PaintFn } from './mowerArt';
import { mowerTextureKey } from './placeholderArt';
import type { TileFrameName } from './tileFrames';
import { equippedPaint, mowerMastered } from '../../meta/economy';
import type { Save } from '../../meta/save/schema';

/**
 * How cosmetics look (docs/GDD.md §10): mower paints and lawn stripe patterns, in the shop
 * previews, the garage and in play.
 */

/** Small deterministic hash for camo blotches (stable textures, no Math.random). */
function blotch(x: number, y: number): number {
  const h = Math.imul(((x >> 1) * 73856093) ^ ((y >> 1) * 19349663), 2654435761);
  return (h >>> 0) % 3;
}

const PAINTS: Record<string, PaintFn> = {
  'paint-mint': (slot) => ['#5fd3a6', '#9ff0cf', '#2f9a76'][slot]!,
  'paint-sunset': (slot) => ['#f26b5b', '#ffb36b', '#b8406b'][slot]!,
  'paint-gold': (slot) => ['#d4a017', '#ffe38a', '#9c7410'][slot]!,
  // All-Stars rewards.
  'paint-fern': (slot) => ['#2f7d45', '#7ccf6a', '#1a4f2b'][slot]!,
  'paint-barnred': (slot) => ['#b8302a', '#e8684e', '#761c18'][slot]!,
  'paint-moonlight': (slot) => ['#a9b8d8', '#eef3ff', '#56648c'][slot]!,
  // The final prize: trophy white, gold along its edges, navy shade.
  'paint-champion': (slot) => ['#f4f2ea', '#ffd23f', '#2a2f6a'][slot]!,
  // The full game's thank-you: lawnmower.'s own orange, with a cream trim.
  'paint-thanks': (slot) => ['#f08a24', '#fff1c9', '#a8521a'][slot]!,
  // Telling five friends: a megaphone's cherry red, ringed in white.
  'paint-megaphone': (slot, _x, y) =>
    slot === 1
      ? '#ff8f80'
      : slot === 2
        ? '#8e1a24'
        : Math.floor(y / 2) % 3 === 2
          ? '#fff4ea'
          : '#e23a3a',
  // The featured week's winner: royal purple with gold trim, a gold band along the top.
  'paint-crown': (slot, _x, y) =>
    slot === 1 ? '#ffd23f' : slot === 2 ? '#3a1a5e' : y <= 3 ? '#e8b923' : '#6b3fa0',
  // Every lawn mowed: a racing checker of white and black in two-pixel squares, flag-red edges.
  'paint-finish': (slot, x, y) =>
    slot === 1
      ? '#ff5a4f'
      : slot === 2
        ? '#14161c'
        : ((x >> 1) + (y >> 1)) % 2 === 0
          ? '#f4f4f0'
          : '#2b2e36',
  // Two-pixel blotches of olive, tan and bark over the body; light/dark edges stay readable.
  'paint-camo': (slot, x, y) =>
    slot === 1
      ? '#9aa86a'
      : slot === 2
        ? '#3b4428'
        : ['#5b6b3a', '#a08a5a', '#46512c'][blotch(x, y)]!,
};

/** Stellar Tie-Dye's bands, round the rainbow: the paint, its lit edge, its shade. */
const TIE_DYE = [
  ['#ff4d6d', '#ff9aae', '#a8243f'],
  ['#ff9f1c', '#ffc976', '#b0620a'],
  ['#ffe45e', '#fff3a8', '#b39a1a'],
  ['#4cd97b', '#9df0b6', '#1f8a45'],
  ['#3aa8ff', '#92d0ff', '#1a5fa8'],
  ['#a66cff', '#cfadff', '#5f2fae'],
] as const;

/** Secret Garden's flowers. */
const BLOOMS = ['#ff7eb6', '#ffffff', '#c69bff', '#ff9f1c'] as const;

/** A small fixed hash of a pixel, for the twinkles (no Math.random: every redraw agrees). */
function twinkle(x: number, y: number): number {
  return (Math.imul((x * 73856093) ^ (y * 19349663), 2654435761) >>> 0) % 97;
}

/**
 * Paints that move (owner, 2026-10-29): the paint at `phase` (0 to 1, round and round) for a
 * mower frame `size` pixels square. Their textures are redrawn a few times a second, so every
 * sprite wearing one moves with it: garage, menu, level card and lawn alike.
 */
const ANIMATED: Record<string, (phase: number, size: number) => PaintFn> = {
  // Every star in the game: a rainbow tie-dye spiral turning, white stars twinkling over it.
  'paint-stellar': (phase, size) => (slot, x, y) => {
    const c = size / 2;
    if (slot === 0 && (twinkle(x, y) + Math.floor(phase * 97)) % 97 < 2) return '#ffffff';
    const turn = Math.atan2(y - c, x - c) / (2 * Math.PI);
    const reach = Math.hypot(x - c, y - c) / (size / 3);
    const band = Math.floor((((turn + reach - phase) % 1) + 1) * TIE_DYE.length) % TIE_DYE.length;
    return TIE_DYE[band]![slot];
  },
  // Every secret yard: a dark hedge with ivy, gilded edges like a garden gate, flowers opening in a
  // wave across it, and in each turn the gold glint that shows where a yard hides, sweeping over.
  'paint-secret': (phase, size) => (slot, x, y) => {
    // The glint: a gold band crossing on the diagonal over most of each turn.
    const glint = Math.abs(x + y - ((phase / 0.75) * (size * 2 + 8) - 4));
    if (glint < 1.5) return '#fffbe0';
    if (glint < 3) return slot === 2 ? '#b8860b' : '#ffd23f';
    if (slot === 1) return '#ffd23f';
    if (slot === 2) return '#0f3a18';
    const h = twinkle(x, y);
    // A flower on one pixel in three, open while the wave passes over it.
    if (h % 3 === 0) {
      const open = (((x / size - phase) % 1) + 1) % 1 < 0.45;
      return open ? BLOOMS[(h >> 2) % BLOOMS.length]! : '#3f8f3a';
    }
    // Ivy: bright leaves on the hedge.
    return h % 2 === 0 ? '#4caf46' : '#236b2c';
  },
};

/** One turn of an animated paint, and how often it is redrawn. */
const PAINT_PERIOD_MS = 2400;
const PAINT_STEP_MS = 125;

/** Redraws for animated textures, by key (a painted sheet, then any trim made from it). */
const redraws = new Map<string, () => void>();
/**
 * The scenes showing each animated texture: only those are redrawn, and only while one of those
 * scenes runs (owner, 2026-11-04: every paint the Garage had ever shown was redrawn eight times a
 * second for the rest of the session, mid-lawn too).
 */
const shownIn = new Map<string, Set<Phaser.Scene>>();
const watched = new WeakSet<Phaser.Scene>();
let ticking: ReturnType<typeof setInterval> | null = null;

/** Draws `key` now, and again every step while `scene` (or another that uses it) runs. */
function animate(scene: Phaser.Scene, key: string, draw?: () => void): void {
  if (draw) redraws.set(key, draw);
  const redraw = redraws.get(key);
  if (!redraw) return;
  let scenes = shownIn.get(key);
  if (!scenes) shownIn.set(key, (scenes = new Set()));
  scenes.add(scene);
  if (!watched.has(scene)) {
    watched.add(scene);
    scene.events.once('shutdown', () => {
      watched.delete(scene);
      for (const [k, set] of shownIn) {
        set.delete(scene);
        if (set.size === 0) shownIn.delete(k);
      }
      if (shownIn.size === 0 && ticking !== null) {
        clearInterval(ticking);
        ticking = null;
      }
    });
  }
  redraw();
  ticking ??= setInterval(() => {
    // (In the order they were made: a painted sheet before any trim drawn over it.)
    for (const [k, draw] of redraws) if (shownIn.has(k)) draw();
  }, PAINT_STEP_MS);
}

/** Where an animated paint is in its turn, now. */
const paintPhase = () => (Date.now() % PAINT_PERIOD_MS) / PAINT_PERIOD_MS;

export function isPaint(cosmeticId: string): boolean {
  return cosmeticId in PAINTS || cosmeticId in ANIMATED;
}

/**
 * Texture key of `mowerId` wearing a paint cosmetic, generated on first use (same layout as the
 * mower's own sheet: north, east, south, west, broken).
 */
export function paintedMowerTexture(
  scene: Phaser.Scene,
  mowerId: MowerId,
  paintId: string,
): string {
  const key = `${mowerTextureKey(mowerId)}+${paintId}`;
  const moving = ANIMATED[paintId];
  const paint = PAINTS[paintId];
  if (!paint && !moving) return mowerTextureKey(mowerId);
  if (scene.textures.exists(key)) {
    if (moving) animate(scene, key);
    return key;
  }
  const M = mowerFrameSize(mowerId);
  const tex = scene.textures.createCanvas(key, M * 5, M);
  if (!tex) return mowerTextureKey(mowerId);
  for (let i = 0; i < 5; i++) tex.add(i, 0, i * M, 0, M, M);
  if (moving) {
    animate(scene, key, () => {
      tex.context.clearRect(0, 0, M * 5, M);
      drawPaintedSheet(tex, mowerId, moving(paintPhase(), M));
      tex.refresh();
    });
    return key;
  }
  drawPaintedSheet(tex, mowerId, paint!);
  tex.refresh();
  return key;
}

/** A mower's sheet (north, east, south, west, broken) in a paint, onto a canvas texture. */
function drawPaintedSheet(
  tex: Phaser.Textures.CanvasTexture,
  mowerId: MowerId,
  paint: PaintFn,
): void {
  const M = mowerFrameSize(mowerId);
  for (const dir of [Dir.N, Dir.E, Dir.S, Dir.W]) {
    drawMowerFrame(
      (x, y, color) => {
        tex.context.fillStyle = color;
        tex.context.fillRect(dir * M + x, y, 1, 1);
      },
      mowerId,
      dir,
      false,
      paint,
    );
  }
  // The wreck keeps its paint job (dulled and sooty).
  drawMowerFrame(
    (x, y, color) => {
      tex.context.fillStyle = color;
      tex.context.fillRect(4 * M + x, y, 1, 1);
    },
    mowerId,
    Dir.E,
    true,
    paint,
  );
}

/**
 * The player's own mower sheet: wearing the paint equipped on it, if any, and its gold Master
 * trim once every upgrade is bought.
 */
export function ownMowerTexture(
  scene: Phaser.Scene,
  mowerId: MowerId,
  save: Readonly<Save>,
): string {
  const paint = equippedPaint(save, mowerId);
  const key = paint ? paintedMowerTexture(scene, mowerId, paint) : mowerTextureKey(mowerId);
  return mowerMastered(save, mowerId) ? masterTrimTexture(scene, key) : key;
}

/** The Master trim's gold: the outline, lit and shaded. */
const TRIM = { lit: [255, 210, 63], shade: [184, 134, 11] } as const;

/**
 * Mower mastery (economy pass 2): a mower sheet with its gold Master trim, generated on first use.
 * The sprite's outline turns gold (lit along the top and left, deeper below and right), so it
 * works over any paint job and over drawn art alike. Frames as in the source sheet.
 */
export function masterTrimTexture(scene: Phaser.Scene, sourceKey: string): string {
  const key = `${sourceKey}+master`;
  if (scene.textures.exists(key)) {
    if (redraws.has(key)) animate(scene, key);
    return key;
  }
  const source = scene.textures.get(sourceKey);
  const image = source.getSourceImage() as CanvasImageSource & { width: number; height: number };
  const tex = scene.textures.createCanvas(key, image.width, image.height);
  if (!tex) return sourceKey;
  // The same frames as the source sheet.
  for (const name of source.getFrameNames()) {
    const f = source.get(name);
    tex.add(name, 0, f.cutX, f.cutY, f.cutWidth, f.cutHeight);
  }
  const draw = () => drawTrim(tex, image);
  // (An animated paint underneath: the trim is drawn again after it, each step.)
  if (redraws.has(sourceKey)) animate(scene, key, draw);
  else draw();
  return key;
}

/** The Master trim over a copy of `image`, onto `tex`. */
function drawTrim(
  tex: Phaser.Textures.CanvasTexture,
  image: CanvasImageSource & { width: number; height: number },
): void {
  const ctx = tex.context;
  ctx.clearRect(0, 0, image.width, image.height);
  ctx.drawImage(image, 0, 0);
  const { width: w, height: h } = image;
  const data = ctx.getImageData(0, 0, w, h);
  const px = data.data;
  const solid = (x: number, y: number) =>
    x >= 0 && y >= 0 && x < w && y < h && px[(y * w + x) * 4 + 3]! > 0;
  const out = new Uint8ClampedArray(px);
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      if (!solid(x, y)) continue;
      // An edge pixel: open to the outside on some side.
      const openUp = !solid(x, y - 1) || !solid(x - 1, y);
      const openDown = !solid(x, y + 1) || !solid(x + 1, y);
      if (!openUp && !openDown) continue;
      const [r, g, b] = openUp ? TRIM.lit : TRIM.shade;
      const i = (y * w + x) * 4;
      out[i] = r;
      out[i + 1] = g;
      out[i + 2] = b;
    }
  }
  ctx.putImageData(new ImageData(out, w, h), 0, 0);
  tex.refresh();
}

/** One paw print, five tiles square: four toes around a pad (stripes-paws). */
const PAW = ['.#.#.', '#...#', '..#..', '.###.', '.###.'] as const;

/** Stripe pattern cosmetics: which mowed-tile frame each lawn tile shows. */
const STRIPES: Record<string, (x: number, y: number) => TileFrameName> = {
  'stripes-checker': (x, y) => ((x + y) % 2 === 0 ? 'mowedN' : 'mowedS'),
  'stripes-diagonal': (x, y) => ((x + y) % 4 < 2 ? 'mowedN' : 'mowedS'),
  'stripes-wide': (x) => (Math.floor(x / 2) % 2 === 0 ? 'mowedN' : 'mowedS'),
  'stripes-plaid': (x, y) =>
    x % 2 === 0 ? (y % 2 === 0 ? 'mowedN' : 'mowedE') : y % 2 === 0 ? 'mowedS' : 'mowedW',
  // All-Stars rewards. Backyard: pickets (columns) on a rail every third row.
  'stripes-picket': (x, y) => (y % 3 === 1 ? 'mowedE' : x % 2 === 0 ? 'mowedN' : 'mowedS'),
  // Golf: an argyle lattice of diamonds over the stripes.
  'stripes-argyle': (x, y) =>
    (x + y) % 4 === 0 || (x - y + 400) % 4 === 0 ? 'mowedE' : x % 2 === 0 ? 'mowedN' : 'mowedS',
  // Rolling Meadows: rows rolling like the hills, rising and falling across the lawn.
  'stripes-meadow': (x, y) =>
    (y + Math.round(Math.sin(x / 2) * 1.5) + 8) % 4 < 2 ? 'mowedE' : 'mowedW',
  // Estate: a wide royal plaid, two-tile squares crossed by bands.
  'stripes-royal': (x, y) =>
    y % 4 === 3
      ? 'mowedW'
      : (Math.floor(x / 2) + Math.floor(y / 2)) % 2 === 0
        ? 'mowedN'
        : 'mowedS',
  // The critter tag: dark paw prints on an even, light cut, each row of prints half a step on.
  'stripes-paws': (x, y) => {
    const band = Math.floor(y / 6);
    const lx = (x + band * 4) % 8;
    const ly = y % 6;
    return ly < 5 && lx < 5 && PAW[ly]![lx] === '#' ? 'mowedS' : 'mowedN';
  },
  // Telling five friends: the word spreading, in ripples rolling out from the top left corner.
  'stripes-chat': (x, y) =>
    Math.floor(Math.hypot(x + 0.5, y + 0.5)) % 4 < 2 ? 'mowedS' : 'mowedN',
};
/** The standard look: alternating columns. */
const DEFAULT_STRIPES = (x: number): TileFrameName => (x % 2 === 0 ? 'mowedN' : 'mowedS');

export function stripeFrame(patternId: string | null, x: number, y: number): TileFrameName {
  return (patternId ? STRIPES[patternId] : undefined)?.(x, y) ?? DEFAULT_STRIPES(x);
}
